import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

const FeedbackCtx = createContext(null);
export const useFeedback = () => useContext(FeedbackCtx);

export const FeedbackProvider = ({ children }) => {
  const [enquiry, setEnquiry] = useState(null);
  const [toasts, setToasts] = useState([]);

  const toast = useCallback((msg) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4500);
  }, []);

  const openEnquiry = useCallback((context, kind = "enquiry") => setEnquiry({ context, kind }), []);

  return (
    <FeedbackCtx.Provider value={{ toast, openEnquiry }}>
      {children}
      <EnquiryModal enquiry={enquiry} onClose={() => setEnquiry(null)} toast={toast} />
      <div className="fixed bottom-6 left-1/2 z-[130] -translate-x-1/2 space-y-2">
        <AnimatePresence>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              data-testid="toast-message"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-3 rounded-full border border-[#D4AF37]/40 bg-[#101B36] px-5 py-3 text-sm text-[#F3E5AB] shadow-2xl"
            >
              <CheckCircle2 size={16} className="shrink-0 text-[#D4AF37]" />
              {t.msg}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </FeedbackCtx.Provider>
  );
};

const EnquiryModal = ({ enquiry, onClose, toast }) => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({ name: "", phone: "", message: "" });
  }, [enquiry]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", {
        ...form,
        kind: enquiry.kind,
        meta: { context: enquiry.context },
      });
      toast("Thank you — our concierge will reach out to you shortly.");
      onClose();
    } catch {
      toast("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {enquiry && (
        <motion.div
          data-testid="enquiry-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="glass-card w-full max-w-md rounded-2xl p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-start justify-between">
              <div>
                <p className="eyebrow mb-2">Private Enquiry</p>
                <h3 className="font-serif text-2xl text-white">{enquiry.context}</h3>
              </div>
              <button
                data-testid="enquiry-close-button"
                onClick={onClose}
                className="rounded-full border border-white/10 p-2 text-[#79756E] transition-colors hover:border-[#D4AF37]/50 hover:text-[#E6C265]"
              >
                <X size={16} />
              </button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <input
                data-testid="enquiry-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className="lux-input"
              />
              <input
                data-testid="enquiry-phone-input"
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone / WhatsApp number"
                className="lux-input"
              />
              <textarea
                data-testid="enquiry-message-input"
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Anything we should know? (occasion, budget, design…)"
                className="lux-input resize-none"
              />
              <button data-testid="enquiry-submit-button" type="submit" disabled={busy} className="btn-gold w-full disabled:opacity-60">
                {busy ? "Sending…" : "Request a Callback"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
